import styled, { keyframes } from "styled-components";

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

export const DocContainer = styled.div`
  --doc-bg: #e3e8ea;
  --doc-primary: #004d2c;
  --doc-emerald: #10b981;
  --doc-text: #374151;
  --doc-header: #1f2937;
  
  background: var(--doc-bg);
  min-height: calc(100vh - 64px);
  font-family: 'Metropolis', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: var(--doc-text);
  margin: -1rem;
`;

export const TopNav = styled.div`
  background: linear-gradient(135deg, #064e3b, #065f46, #022c22);
  padding: 0 20px;
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 4px;
  overflow-x: auto;
  width: 100%;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  .brand {
    font-size: 13px;
    font-weight: 700;
    color: #ffffff;
    padding: 14px 16px 14px 0;
    margin-right: 8px;
    white-space: nowrap;
    border-right: 1px solid rgba(255,255,255,0.15);
    
    span { color: #34d399; }
  }

  a {
    font-size: 10px;
    color: rgba(255,255,255,0.75);
    text-decoration: none;
    padding: 14px 12px;
    white-space: nowrap;
    transition: all 0.2s;
    border-bottom: 2px solid transparent;

    &:hover {
      color: #ffffff;
      background: rgba(255,255,255,0.15);
    }

    &.active {
      color: #ffffff;
      background: rgba(255,255,255,0.25);
      border-bottom-color: #10b981;
    }
  }
`;

export const DocWrapper = styled.div`
  max-width: 1100px;
  width: 100%;
  margin: 0 auto;
  padding: 32px 20px;

  .header {
    text-align: center;
    margin-bottom: 40px;
    h1 { font-size: 30px; color: #111827; font-weight: bold; letter-spacing: -0.5px; }
    .tagline { color: #004d2c; font-size: 14px; margin-top: 8px; }
    .subtitle { color: #475569; font-size: 11px; margin-top: 4px; }
    .dot { display: inline-block; width: 10px; height: 10px; background: #22d3ee; border-radius: 50%; margin-right: 10px; animation: ${pulse} 2s infinite; }
  }

  .section-hdr {
    font-size: 16px;
    color: #111827;
    font-weight: bold;
    margin: 36px 0 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #cbd5e1;
    display: flex;
    align-items: center;
    gap: 10px;

    .tag {
      font-size: 10px;
      font-weight: 600;
      padding: 3px 10px;
      border-radius: 20px;
      background: #0f4c35;
      color: #ffffff;
      letter-spacing: 0.3px;
      &.gold { background: #92400e; }
      &.rose { background: #9f1239; }
      &.emerald { background: #065f46; }
    }
    .count { font-weight: normal; color: #64748b; font-size: 11px; }
  }

  .card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 16px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
    transition: all 0.3s ease;
    
    &:hover {
      border-color: #a7f3d0;
      box-shadow: 0 4px 12px rgba(0,0,0,0.08);
      transform: translateY(-1px);
    }

    h3 { font-size: 13px; color: #1f2937; margin: 0; }
    p, li { font-size: 11px; line-height: 1.7; color: #6b7280; }
    ul { padding-left: 16px; margin: 6px 0; }
    li { margin: 3px 0; }
    table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 10px; }
    th, td { border: 1px solid #1e293b; padding: 6px 10px; text-align: left; }
    th { background: #f3f4f6; color: #4b5563; font-weight: 500; }
    td { color: #374151; }
    strong { color: #1f2937; }
    pre { background: #f3f4f6; padding: 12px; border-radius: 6px; font-size: 10px; overflow-x: auto; margin: 8px 0; color: #1f2937; white-space: pre-wrap; }
    code { background: #f3f4f6; padding: 1px 5px; border-radius: 3px; font-size: 10px; color: #1f2937; }
    svg { width: 100%; height: auto; display: block; }
  }

  .card-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
    cursor: pointer;
    user-select: none;

    .arrow {
      font-size: 10px;
      color: #004d2c;
      transition: transform 0.2s;
      flex-shrink: 0;
      &.open { transform: rotate(90deg); }
    }
  }

  .card-body {
    font-size: 11px;
    line-height: 1.7;
    color: #6b7280;
    overflow: hidden;
    transition: max-height 0.3s ease;
    
    &.collapsed { max-height: 0; }
    &.open { max-height: 5000px; padding-top: 4px; }
  }

  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin: 12px 0; @media (max-width: 768px) { grid-template-columns: 1fr; } }
  .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; margin: 12px 0; @media (max-width: 768px) { grid-template-columns: 1fr; } }

  .card-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    &.cyan { background: #004d2c; }
    &.emerald { background: #059669; }
    &.violet { background: #7c3aed; }
    &.amber { background: #d97706; }
    &.rose { background: #e11d48; }
    &.sky { background: #0284c7; }
    &.gold { background: #d97706; }
    &.teal { background: #0d9488; }
    &.slate { background: #6b7280; }
  }

  .badge {
    display: inline-block;
    font-size: 9px;
    padding: 2px 8px;
    border-radius: 10px;
    font-weight: 500;
    margin: 1px 2px;
    &.cyan { background: rgba(0,77,44,0.1); color: #004d2c; }
    &.emerald { background: rgba(5,150,105,0.15); color: #059669; }
    &.violet { background: rgba(124,58,237,0.1); color: #7c3aed; }
    &.amber { background: rgba(217,119,6,0.12); color: #d97706; }
    &.rose { background: rgba(225,29,72,0.1); color: #e11d48; }
    &.slate { background: rgba(107,114,128,0.15); color: #6b7280; }
    &.gold { background: rgba(217,119,6,0.12); color: #d97706; }
  }

  .quote { border-left: 3px solid #22d3ee; padding: 10px 16px; margin: 10px 0; background: rgba(8,51,68,0.2); border-radius: 0 6px 6px 0; font-size: 12px; color: #374151; font-style: italic; }
  .highlight-box { background: rgba(0,77,44,0.04); border: 1px solid rgba(0,77,44,0.15); border-radius: 8px; padding: 12px; margin: 8px 0; font-size: 10px; color: #374151; strong { color: #004d2c; } }
  
  .stat-row { display: flex; gap: 12px; flex-wrap: wrap; margin: 12px 0; }
  .stat { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px 16px; flex: 1; min-width: 100px; text-align: center; .num { font-size: 20px; font-weight: 700; color: #004d2c; } .label { font-size: 9px; color: #6b7280; margin-top: 4px; } }

  .flow-step {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 10px 0;
    border-bottom: 1px solid #1e293b;
    &:last-child { border-bottom: none; }
    .flow-num {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: bold;
      flex-shrink: 0;
      &.cyan { background: rgba(8,51,68,0.5); color: #22d3ee; }
      &.emerald { background: rgba(6,78,59,0.5); color: #34d399; }
      &.amber { background: rgba(120,53,15,0.5); color: #fbbf24; }
      &.rose { background: rgba(136,19,55,0.5); color: #fb7185; }
    }
    .flow-content { flex: 1; h4 { font-size: 11px; color: #1f2937; margin-bottom: 2px; } p { font-size: 10px; color: #4b5563; line-height: 1.5; } }
  }

  .accordion-item { border: 1px solid #e5e7eb; border-radius: 8px; margin-bottom: 8px; overflow: hidden; background: #ffffff; }
  .accordion-trigger { width: 100%; text-align: left; font-family: inherit; font-size: 11px; padding: 12px 16px; background: #f9fafb; border: none; color: #1f2937; cursor: pointer; display: flex; align-items: center; justify-content: space-between; &:hover { background: #f3f4f6; } .acc-arrow { font-size: 10px; color: #004d2c; transition: transform 0.2s; } &.active .acc-arrow { transform: rotate(180deg); } }
  .accordion-body { padding: 0 16px 12px; font-size: 10px; color: #6b7280; line-height: 1.6; display: none; &.open { display: block; } }
`;

export const ExpandableCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  transition: all 0.3s ease;
  &:hover { border-color: #a7f3d0; box-shadow: 0 4px 12px rgba(0,0,0,0.08); transform: translateY(-1px); }
  .clickable { cursor: pointer; }
  .card-header { display: flex; align-items: center; gap: 10px; margin-bottom: 0; }
  .card-body { overflow: hidden; transition: max-height 0.3s ease; &.collapsed { max-height: 0; } &.open { max-height: 5000px; padding-top: 12px; } }
`;

export const Grid2 = styled.div` display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin: 12px 0; @media (max-width: 768px) { grid-template-columns: 1fr; } `;
export const Grid3 = styled.div` display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; margin: 12px 0; @media (max-width: 768px) { grid-template-columns: 1fr; } `;
